import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismIcon, PrismMetricBlock } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import type { BeniTooltipData } from 'lumen/components/hotel/benchmark-insight/tooltips/beni-tooltip';
import type { BeniMetaComparisonSet } from 'lumen/models';
import assignTextSuccessCriticalClass from 'lumen/modifiers/assign-text-success-critical-class';
import {
  FORMATTED_KPI_INDEXES,
  FORMATTED_KPIS,
  type Kpi,
  KPI_INDEX_LONG_TRANSLATIONS,
  KPI_INDEXES_FOR_KPIS,
  KPI_LONG_TRANSLATIONS,
  NUMBER_FORMATS,
  NUMBER_STYLES,
  type ReferencePeriod,
} from 'lumen/utils/constants/benchmark-insight/beni-constant-values';

interface HotelBenchmarkInsightTooltipsSharedTooltipMetricBlockSignature {
  Args: {
    primaryComparisonSetMeta: BeniMetaComparisonSet | null;
    referencePeriod: ReferencePeriod;
    selectedKpi: Kpi;
    tooltipData: BeniTooltipData;
  };
}

export default class HotelBenchmarkInsightTooltipsSharedTooltipMetricBlock extends Component<HotelBenchmarkInsightTooltipsSharedTooltipMetricBlockSignature> {
  @service declare access: Services['access'];
  @service('benchmark-insight/beni-service')
  declare benchmarkInsightBeniService: Services['benchmark-insight/beni-service'];
  @service declare intl: Services['intl'];

  get formattedIndex(): string {
    const { tooltipData, selectedKpi } = this.args;

    if (!tooltipData.primaryComparisonSetBreakdown) {
      return this.intl.t('general.missing');
    }

    const formattedIndexKey =
      FORMATTED_KPI_INDEXES[KPI_INDEXES_FOR_KPIS[selectedKpi]];
    return tooltipData.primaryComparisonSetBreakdown?.current.total.index[
      formattedIndexKey
    ];
  }

  get formattedKpiDiff(): string {
    const { tooltipData, selectedKpi } = this.args;

    if (
      !tooltipData.primaryComparisonSetBreakdown ||
      !tooltipData.primaryComparisonSetBreakdown.current.total.difference
    ) {
      return this.intl.t('general.missing');
    }

    const formattedKpiDiffKey = FORMATTED_KPIS[selectedKpi];
    return tooltipData.primaryComparisonSetBreakdown.current.total.difference[
      formattedKpiDiffKey
    ];
  }

  get indexLongTranslation(): string {
    return this.intl.t('benchmark_insight.index.expansion', {
      index: KPI_INDEXES_FOR_KPIS[this.args.selectedKpi].toUpperCase(),
      expansion: this.intl.t(
        KPI_INDEX_LONG_TRANSLATIONS[
          KPI_INDEXES_FOR_KPIS[this.args.selectedKpi]
        ],
      ),
    });
  }

  get kpiDiff(): number | null {
    const { tooltipData, selectedKpi } = this.args;
    if (!tooltipData.primaryComparisonSetBreakdown?.current.total.difference) {
      return null;
    }

    return (
      tooltipData.primaryComparisonSetBreakdown?.current.total.difference[
        selectedKpi
      ] ?? null
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get kpiDiffIcon() {
    if (!this.kpiDiff || this.formattedKpiDiff === '0pt') {
      return;
    }

    if (this.kpiDiff > 0) {
      return {
        color: 'success',
        name: 'arrow-up',
        size: 'extra-small',
      } as const;
    }

    return {
      color: 'critical',
      name: 'arrow-down',
      size: 'extra-small',
    } as const;
  }

  get maxRanking(): string {
    const { tooltipData } = this.args;

    const maxRankingValue =
      tooltipData.primaryComparisonSetBreakdown?.current.total.ranking.size ??
      null;

    return this.benchmarkInsightBeniService.formatNumber(
      maxRankingValue,
      NUMBER_FORMATS.RANKING,
      NUMBER_STYLES.DEFAULT,
    );
  }

  get ranking(): string {
    const { tooltipData, selectedKpi } = this.args;
    const rankingValue =
      tooltipData.primaryComparisonSetBreakdown?.current.total.ranking[
        selectedKpi
      ] ?? null;

    return this.benchmarkInsightBeniService.formatNumber(
      rankingValue,
      NUMBER_FORMATS.RANKING,
      NUMBER_STYLES.DEFAULT,
    );
  }

  get roundedIndex(): number | null {
    const { tooltipData, selectedKpi } = this.args;

    const rawValue =
      tooltipData.primaryComparisonSetBreakdown?.current.total.index[
        KPI_INDEXES_FOR_KPIS[selectedKpi]
      ] ?? null;

    if (!rawValue) {
      return null;
    }

    return this.benchmarkInsightBeniService.roundIndex(rawValue);
  }

  get selectedKpiTranslation(): string {
    const { selectedKpi } = this.args;

    return KPI_LONG_TRANSLATIONS[selectedKpi];
  }

  <template>
    <div class="tooltip-metric-block">
      {{#if this.access.hasBenchmarkInsightRanking}}
        <PrismMetricBlock @align="left" @allowLabelTextWrapping={{true}}>
          <:value>
            <span class="flex items-center justify-center">
              {{t
                "benchmark_insight.ranking_fraction"
                maxRanking=this.maxRanking
                ranking=this.ranking
              }}
            </span>
          </:value>
          <:label>
            <span class="text-200--regular">
              {{t
                "benchmark_insight.hero_chart.reference_ranking_kpi"
                kpi=(t this.selectedKpiTranslation)
              }}
            </span>
          </:label>
        </PrismMetricBlock>
      {{/if}}

      <PrismMetricBlock @align="left" @allowLabelTextWrapping={{true}}>
        <:value>
          <span class="flex flex-row items-center justify-center">
            {{this.formattedKpiDiff}}

            {{#if this.kpiDiffIcon}}
              <PrismIcon
                @color={{this.kpiDiffIcon.color}}
                @name={{this.kpiDiffIcon.name}}
                @size={{this.kpiDiffIcon.size}}
              />
            {{/if}}
          </span>
        </:value>
        <:label>
          <span class="text-200--regular">
            {{t
              "benchmark_insight.hero_chart.kpi_diff"
              kpi=(t this.selectedKpiTranslation)
            }}
          </span>
        </:label>
      </PrismMetricBlock>

      <PrismMetricBlock @align="left" @allowLabelTextWrapping={{true}}>
        <:value>
          {{#if this.roundedIndex}}
            <span {{assignTextSuccessCriticalClass this.roundedIndex 100}}>
              {{this.formattedIndex}}
            </span>
          {{else}}
            {{this.formattedIndex}}
          {{/if}}
        </:value>
        <:label>
          <span class="text-200--regular">
            {{this.indexLongTranslation}}
          </span>
        </:label>
      </PrismMetricBlock>
    </div>
  </template>
}
